import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "Practical ways to share training progress, celebrate competition results, and keep family, friends and club connected to your athletic journey.";

const FAQS = [
  {
    q: 'How often should I post updates to my supporters?',
    a: 'Consistency matters more than frequency. Pick one cadence and stick to it: weekly for an intense training block, per-competition if you race or play regularly, or every 3–6 weeks if your sport is structured around longer training cycles.',
  },
  {
    q: 'Who can see my posts on Sport Fund?',
    a: 'You control the audience on every post: public (anyone), followers only, or supporters only. Save your more personal, behind-the-scenes content for supporters only as a way to reward the people backing you.',
  },
  {
    q: 'What should a good supporter update include?',
    a: 'A short update — even 100 words and a photo — works best when it covers where you are right now, one specific moment from the week, what you\'re working toward next, and a thank-you to recent contributors.',
  },
  {
    q: 'Can I email an update directly to my supporters?',
    a: "Yes — from your dashboard you can email any post to your supporters and followers directly. Save it for moments that matter, like a result, a selection, or a major milestone, so it doesn't become noise.",
  },
  {
    q: 'Does posting updates actually affect how much support I get?',
    a: "Yes. Supporters who can see a specific link between their contribution and an outcome — for example, this week's training paid for by last month's contributions — are more likely to give again and to tell others.",
  },
];

const StrategicStewardship: React.FC = () => {
  usePageMeta('Engaging Your Supporters', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/athlete-celebration.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Engaging Your Supporters' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "pick-a-cadence-and-stick-to-it",
      text: "Pick a cadence and stick to it"
    },
    {
      id: "five-things-that-work-in-an-update",
      text: "Five things that work in an update"
    },
    {
      id: "use-the-visibility-controls",
      text: "Use the visibility controls"
    },
    {
      id: "show-the-link-to-the-money",
      text: "Show the link to the money"
    },
    {
      id: "use-email-when-it-matters",
      text: "Use email when it matters"
    },
    {
      id: "celebrate-the-milestones",
      text: "Celebrate the milestones"
    },
    {
      id: "treat-it-like-training",
      text: "Treat it like training"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Engaging Your Supporters: Keep Family, Friends & Club in the Loop</h1>
      <p className="text-gray-500 mb-8 text-lg">Simple ways to share your progress, celebrate milestones, and make every contributor feel part of the journey.</p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          The best fundraising pages don't feel like fundraising pages — they feel like a window into an athlete's season. Supporters stick around, contribute again, and bring in new supporters when they feel like insiders. This is how you build that feeling, without it becoming a second job.
        </p>

        <h2 id="pick-a-cadence-and-stick-to-it">Pick a cadence and stick to it</h2>
        <p>Consistency matters more than frequency. Pick one of these and commit:</p>
        <ul>
          <li><strong>Weekly</strong> — a short update every Sunday or Monday. Good if your training block is intense and changes a lot.</li>
          <li><strong>Per competition</strong> — a post before, and a post after, every event. Good if you're racing or playing regularly.</li>
          <li><strong>Per training block</strong> — every 3–6 weeks at the end of a block. Good if your sport is structured around big training cycles.</li>
        </ul>
        <p>
          Whatever you pick, your supporters will start expecting it. Don't disappear for two months and then send a long catch-up — three short updates spread out land better than one long one.
        </p>

        <h2 id="five-things-that-work-in-an-update">Five things that work in an update</h2>
        <p>You don't need to write a long essay. A great update can be 100 words and a photo. The shape that works:</p>
        <ol>
          <li><strong>Where I am right now</strong> — current training block, the next target, how it's going.</li>
          <li><strong>One specific thing</strong> — a session, a result, a setback, a moment from the week. Specific beats general every time.</li>
          <li><strong>What I'm working on</strong> — the next milestone, the next race, the next selection.</li>
          <li><strong>Photo or short video</strong> — phone quality. Action shots, behind-the-scenes, kit shots all work.</li>
          <li><strong>Thank-you</strong> — call out a recent contribution by name if you have permission, or thank the group as a whole.</li>
        </ol>

        <h2 id="use-the-visibility-controls">Use the visibility controls</h2>
        <p>Not every post needs to be public. Sport Fund lets you set the audience on every post: public, followers only, or supporters only. A few suggestions:</p>
        <ul>
          <li><strong>Public</strong>: big competition recaps, milestone announcements, your story — anything you'd be happy showing a stranger.</li>
          <li><strong>Followers only</strong>: weekly training updates, mid-block reflections, anything that's interesting to people who care about your journey but isn't world-facing.</li>
          <li><strong>Supporters only</strong>: more personal content — the harder weeks, the setbacks, the things you don't want on a public profile. Reward the people who back you with a closer view.</li>
        </ul>

        <h2 id="show-the-link-to-the-money">Show the link to the money</h2>
        <p>
          When you can, draw a line between a contribution and an outcome. "This week's training was paid for by last month's recurring contributions from X, Y and Z — thank you" turns abstract support into concrete impact. Supporters who can see what they enabled give again, and tell others.
        </p>

        <h2 id="use-email-when-it-matters">Use email when it matters</h2>
        <p>
          From your dashboard, you can email any post to your supporters and followers directly. Don't email every post — that becomes noise. Use it for the moments that matter: a result, a selection, a major milestone, an end-of-season recap.
        </p>

        <h2 id="celebrate-the-milestones">Celebrate the milestones</h2>
        <p>
          When you hit a target — a qualifying time, a selection, a personal best, a season completed — make a moment of it. A post, an email, maybe a short video saying thank-you. These are the moments that get re-shared, that bring in new supporters, and that remind your existing ones why they're with you.
        </p>

        <h2 id="treat-it-like-training">Treat it like training</h2>
        <p>
          Posting updates is a skill. The first few feel awkward, the next five feel routine, by the tenth you've got a voice and a rhythm. Block 20 minutes a week for it. Future-you will thank present-you.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/supporting-an-athlete" className="text-blue-600">Supporting an Athlete</Link> — share this with potential supporters to explain how Sport Fund works from their side.
        </p>
        <ArticleFeedback articleSlug="engaging-your-supporters" />
      </article>
    </div>
  );
};

export default StrategicStewardship;
